/**
 * The tenancy contract (PLAN §11, D13, D14): who the caller is, which tenant
 * they act in, and the tenant-admin surface — users, groups, memberships — plus
 * the vendor's tenant registry. The routes are `tenancyRoutes.ts`, part of the
 * one route table (`routes.ts`).
 *
 * Every request and response body is `.strict()` (CLAUDE.md → the contract),
 * and every id embedded in a DynamoDB key is constrained to a character set
 * without `#`, the key separator, so an id can never forge a key segment
 * (`docs/data-model.md`).
 *
 * **The tenant never appears in a request body.** It comes only from the
 * verified token (D13). `tenantId` in `createTenantRequestSchema` is the id of
 * the tenant being CREATED by the vendor, not the tenant the caller acts in.
 *
 * A group's scope is the domain's `GrantScope` (`@acp/domain/access.js`), and
 * a caller's grants are the domain's `PermissionGrant[]`, so the one pure
 * `isPermitted` check runs in the cloud authorizer and on the node alike.
 */
import type { GrantScope, PermissionGrant } from '@acp/domain/access.js';
import { z } from 'zod';
import { permissionIdSchema, PERMISSION_IDS, type PermissionId } from './permissions.js';

/**
 * A tenant id is chosen by the vendor at creation and never changes. It is a
 * key segment (`T#<tenantId>#…`), hence the strict alphabet.
 */
export const tenantIdSchema = z
  .string()
  .regex(/^[a-z][a-z0-9-]{2,39}$/, 'tenant ids are 3-40 lowercase letters, digits or hyphens, starting with a letter');

/** Group, site and studio ids: a key segment, so no `#` and no whitespace. */
export const entityIdSchema = z.string().regex(/^[A-Za-z0-9][A-Za-z0-9_-]{0,63}$/, 'ids are 1-64 letters, digits, hyphens or underscores');

/** A Cognito `sub`: a UUID. */
export const userIdSchema = z.string().regex(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/, 'user ids are Cognito subs (UUIDs)');

export const emailSchema = z.email().max(254);

/**
 * Where a group's permissions apply (D14): the whole tenant, or named sites
 * and studios — the domain's `GrantScope`.
 */
export const grantScopeSchema = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('tenant') }).strict(),
  z
    .object({
      kind: z.literal('resources'),
      siteIds: z.array(entityIdSchema).max(200),
      studioIds: z.array(entityIdSchema).max(500),
    })
    .strict(),
]);

const permissionListSchema = z.array(permissionIdSchema).max(PERMISSION_IDS.length);

/** One group's contribution to a caller — the domain's `PermissionGrant`, with known ids. */
export const permissionGrantSchema = z.object({ permissions: permissionListSchema, scope: grantScopeSchema }).strict();

// A parsed contract value must BE a domain value, or the shared check would be
// checking something else. Compile-time proofs: these lines stop compiling if
// either shape drifts from `@acp/domain/access.js`.
const scopeFitsDomain: z.infer<typeof grantScopeSchema> extends GrantScope ? true : false = true;
const grantFitsDomain: z.infer<typeof permissionGrantSchema> extends PermissionGrant ? true : false = true;
const domainKindsCovered: GrantScope['kind'] extends z.infer<typeof grantScopeSchema>['kind'] ? true : false = true;
export const DOMAIN_SHAPE_PROOFS = [scopeFitsDomain, grantFitsDomain, domainKindsCovered] as const;

const groupNameSchema = z.string().trim().min(1).max(100);

/** Every tenant's Admin group, and the vendor tenant's Vendor Admin group (D14, D27). */
export const ADMIN_GROUP_ID = 'grp-admin';
export const VENDOR_ADMIN_GROUP_ID = 'grp-vendor-admin';

/**
 * Groups a tenant may not delete, and the permissions (tenant-wide) they must
 * keep: without them nobody could administer the tenant, or the vendor. The
 * server's fence (`apps/cloud-api/src/routes/groups.ts`) and the SPA's group
 * editor (its locked checkboxes) read this one list.
 */
export function protectedPermissions(groupId: string): readonly PermissionId[] | null {
  if (groupId === ADMIN_GROUP_ID) return ['tenant.admin'];
  if (groupId === VENDOR_ADMIN_GROUP_ID) return ['vendor.admin', 'tenant.admin'];
  return null;
}

// ─── resources ───

export const tenantSchema = z.object({ tenantId: tenantIdSchema, name: z.string(), createdAt: z.string() }).strict();
export type Tenant = z.infer<typeof tenantSchema>;

/**
 * Where a user stands (7b): `invited` — created, the invite not yet accepted
 * (no password of their own); `active`; `disabled` — deactivated by an admin:
 * Cognito refuses their sign-in, the API refuses their tokens, and the nodes'
 * directories drop them at the next sync (D20). Users written before this
 * field existed read as `active`.
 */
export const userStatusSchema = z.enum(['invited', 'active', 'disabled']);
export type UserStatus = z.infer<typeof userStatusSchema>;

export const userSchema = z
  .object({
    userId: userIdSchema,
    email: emailSchema,
    createdAt: z.string(),
    status: userStatusSchema,
    /** When the latest invite email was sent, or null (a user made before invites, or by the dev seed). */
    invitedAt: z.string().nullable(),
  })
  .strict();
export type User = z.infer<typeof userSchema>;

/** A user in the admin list: with the ids of their groups. */
export const userSummarySchema = userSchema.extend({ groupIds: z.array(entityIdSchema) }).strict();
export type UserSummary = z.infer<typeof userSummarySchema>;

export const groupSchema = z
  .object({
    groupId: entityIdSchema,
    name: z.string(),
    permissions: permissionListSchema,
    scope: grantScopeSchema,
    createdAt: z.string(),
    updatedAt: z.string(),
  })
  .strict();
export type Group = z.infer<typeof groupSchema>;

// ─── path parameters ───

export const userPathSchema = z.object({ userId: userIdSchema.describe('The user’s id (their Cognito `sub`).') }).strict();
export const groupPathSchema = z.object({ groupId: entityIdSchema.describe('The group’s id.') }).strict();
export const memberPathSchema = groupPathSchema.extend({ userId: userIdSchema.describe('The member’s user id.') }).strict();

// ─── requests ───

export const createTenantRequestSchema = z
  .object({
    tenantId: tenantIdSchema,
    name: z.string().trim().min(1).max(200),
    /** The tenant's first admin: created in Cognito (invitation suppressed) and put in the Admin group. */
    adminEmail: emailSchema,
  })
  .strict();
export type CreateTenantRequest = z.infer<typeof createTenantRequestSchema>;

export const createUserRequestSchema = z
  .object({
    email: emailSchema,
    /** Groups to add the new user to; each must exist in the caller's tenant. */
    groupIds: z.array(entityIdSchema).max(50).optional(),
  })
  .strict();
export type CreateUserRequest = z.infer<typeof createUserRequestSchema>;

export const createGroupRequestSchema = z.object({ name: groupNameSchema, permissions: permissionListSchema, scope: grantScopeSchema }).strict();
export type CreateGroupRequest = z.infer<typeof createGroupRequestSchema>;

export const updateGroupRequestSchema = z
  .object({
    name: groupNameSchema.optional(),
    permissions: permissionListSchema.optional(),
    scope: grantScopeSchema.optional(),
    /**
     * Optimistic concurrency (D35): the `updatedAt` the editor loaded. When it no longer matches, the update is
     * `conflict` and changes nothing. Absent: last writer wins (the CLI).
     */
    ifUpdatedAt: z.string().max(64).optional(),
  })
  .strict()
  .refine((body) => body.name !== undefined || body.permissions !== undefined || body.scope !== undefined, {
    message: 'a group update must change at least one of name, permissions, scope',
  });
export type UpdateGroupRequest = z.infer<typeof updateGroupRequestSchema>;

// ─── responses ───

export const meResponseSchema = z
  .object({
    user: z.object({ userId: userIdSchema, email: emailSchema }).strict(),
    tenant: z.object({ tenantId: tenantIdSchema, name: z.string() }).strict(),
    /** Every permission held somewhere in the tenant, sorted. */
    permissions: z.array(permissionIdSchema),
    /** Where each applies: one entry per group, as the authorizer resolved them (D14). */
    grants: z.array(permissionGrantSchema),
  })
  .strict();
export type MeResponse = z.infer<typeof meResponseSchema>;

export const createdTenantResponseSchema = z.object({ tenant: tenantSchema, adminUser: userSchema }).strict();
export const listTenantsResponseSchema = z.object({ tenants: z.array(tenantSchema) }).strict();
export const userResponseSchema = z.object({ user: userSchema }).strict();
export const listUsersResponseSchema = z.object({ users: z.array(userSummarySchema) }).strict();

/**
 * How an invite email left (7b): `email` — handed to SES; `file` — the
 * development fallback wrote it to a file (`ACP_MAIL_DIR`); `none` — no mailer
 * is configured on this stack, so nothing was sent (resend once one is);
 * `failed` — the transport refused it (SES's sandbox refuses unverified
 * recipients): the user exists, resend once the cause is fixed.
 */
export const inviteDeliverySchema = z.enum(['email', 'file', 'none', 'failed']);
export type InviteDelivery = z.infer<typeof inviteDeliverySchema>;

export const inviteSummarySchema = z.object({ expiresAt: z.string(), delivery: inviteDeliverySchema }).strict();

/** `POST /users` and `POST /users/{userId}/invite`: the user and the invite just sent (never its link). */
export const invitedUserResponseSchema = z.object({ user: userSchema, invite: inviteSummarySchema }).strict();

// ─── accepting an invite (public: the link's token is the credential) ───

/**
 * The invite link's token: `ACPI.<inviteId>.<secret>`, carried in the link's
 * URL fragment (never sent to a server by the browser) and posted in a body —
 * never a path or query, which land in access logs.
 */
export const inviteTokenSchema = z.string().min(1).max(256);

/** Cognito's pool policy (infrastructure/cognito.tf): 12+ characters with lower case, upper case and a digit. */
export const newPasswordSchema = z
  .string()
  .min(12, 'at least 12 characters')
  .max(256)
  .regex(/[a-z]/, 'a lower-case letter')
  .regex(/[A-Z]/, 'an upper-case letter')
  .regex(/[0-9]/, 'a digit');

export const inspectInviteRequestSchema = z.object({ token: inviteTokenSchema }).strict();
export const inspectInviteResponseSchema = z.object({ email: emailSchema, tenantName: z.string(), expiresAt: z.string() }).strict();
export type InspectInviteResponse = z.infer<typeof inspectInviteResponseSchema>;

export const acceptInviteRequestSchema = z.object({ token: inviteTokenSchema, password: newPasswordSchema }).strict();
export const acceptInviteResponseSchema = z.object({ email: emailSchema }).strict();
export const groupResponseSchema = z.object({ group: groupSchema }).strict();
export const groupWithMembersResponseSchema = z.object({ group: groupSchema, memberIds: z.array(userIdSchema) }).strict();
export const listGroupsResponseSchema = z.object({ groups: z.array(groupSchema) }).strict();
